// SupplierListingCard - Individual supplier listing within a master product
import { useState, useEffect, useRef } from 'react'
import { MapPin, Package, Truck, ChevronDown, Heart, ShoppingCart } from 'lucide-react'
import { formatBackorderAvailability, formatEarliestDate } from '../../utils/datetime'
import clsx from 'clsx'
import { VanStockBadge } from './VanStockBadge'
import { BackorderBadge } from './BackorderBadge'
import { InStockBadge } from './InStockBadge'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from '../engineer/favourites/useFavouriteToggle'
import { HOME_TAB_ENABLED } from '../../config/features'
import type { SupplierListing, CartItem } from '../../types'
import { perUnitPrice } from '../../utils/pricing'

export type FulfillmentMethod = 'pickup' | 'delivery'

export interface SupplierListingCardProps {
  listing: SupplierListing           // Default (best) branch
  branches?: SupplierListing[]       // All branches for this raw product
  isBestPrice?: boolean
  isSamePrice?: boolean
  isClosest?: boolean
  cart?: CartItem[]
  onClick?: (listing: SupplierListing) => void
  // Selection API — the page holds the single selected offer + method +
  // quantity; the card renders as a radio option. Tapping a Pickup or
  // Delivery tile selects that method on the currently active branch.
  quantity: number
  isSelected: boolean
  selectedMethod?: FulfillmentMethod
  onSelect: (
    listing: SupplierListing,
    method: FulfillmentMethod,
  ) => void
  onBranchChange?: (listing: SupplierListing) => void
  // Order-on-Demand: live quantity-specific date check. Provided only by the
  // master detail page; returns the earliest collection date for the given
  // quantity at that branch (null if unavailable).
  onCheckQuantity?: (
    supplierId: string | number,
    productCode: string,
    branchId: string | number | null,
    quantity: number,
  ) => Promise<{
    collection: string | null
    delivery: string | null
    backorderCollection?: string | null
    backorderDelivery?: string | null
  }>
  // Bubbles the effective (DC-checked when applicable, else branch-level)
  // fulfillment dates AND the in-flight DC-check state for the ACTIVE branch
  // back to the page — so the sticky footer summary shows the same date the
  // card is displaying AND the page-level Add button waits for the check to
  // resolve before it goes green. Without this, the footer reads the
  // branch-level earliestDate while the card is still resolving a DC-tier
  // quantity, and Add could be tapped against a stale date.
  onResolvedDates?: (
    listing: SupplierListing,
    dates: {
      pickup: string | null
      delivery: string | null
      isChecking: boolean
      // Backorder dates on their own (qty-matched for API-date suppliers),
      // separate from the effective dates above — the page's "can this be
      // backordered" gate must not mistake a normal branch date for one.
      backorderPickup: string | null
      backorderDelivery: string | null
    },
  ) => void
}

export function SupplierListingCard({
  listing,
  branches,
  isBestPrice = false,
  isSamePrice = false,
  isClosest = false,
  cart = [],
  onClick,
  quantity,
  isSelected,
  selectedMethod,
  onSelect,
  onBranchChange,
  onCheckQuantity,
  onResolvedDates,
}: SupplierListingCardProps) {
  const [isProductInfoOpen, setIsProductInfoOpen] = useState(false)
  const [isBranchSelectorOpen, setIsBranchSelectorOpen] = useState(false)
  const [imageError, setImageError] = useState(false)
  // Order-on-Demand: quantity-specific DC collection date (master detail only).
  const [dcCollectionDate, setDcCollectionDate] = useState<string | null>(null)
  const [dcDeliveryDate, setDcDeliveryDate] = useState<string | null>(null)
  // Qty-matched backorder dates from the probe (API-date suppliers only).
  // Tagged with the qty they were fetched for — like the sheet's freshDcDates
  // — so a stale quote is never used for a different qty.
  const [freshBackorderDates, setFreshBackorderDates] = useState<
    { qty: number; collection: string | null; delivery: string | null } | null
  >(null)
  const [isCheckingDcDate, setIsCheckingDcDate] = useState(false)
  // Snapshot of `quantity` that only advances when the debounce timer fires.
  // The fulfilment tile ("N from DC" + DC date) reads this instead of the live
  // `quantity`, so the number never jumps ahead of the date it belongs to.
  // Live `quantity` still drives the stepper input, Add-to-Cart, and pricing.
  const [settledQuantity, setSettledQuantity] = useState(quantity)
  const dcCheckSeqRef = useRef(0)
  // Composite (qty + branch + supplier + productCode) so branch/supplier
  // swaps re-fire the check, but a rapid needsDcDate false→true churn (e.g.
  // isLoadingStock flickering after a cart mutation) does NOT — the key stays
  // the same, so the guard short-circuits.
  const lastCheckedKeyRef = useRef<string | null>(null)

  const allBranches = branches ?? [listing]

  // BM-002 follow-up: highlight branches already represented in the cart
  // with an inline "Items in basket" tag inside the pickup-branch selector
  // so the engineer sees at a glance which branches would consolidate the
  // trip. Cart is authoritative here (via the `cart` prop) — same source
  // BM-002 uses to boost basket branches to the top of the group.
  const basketBranchIds = new Set(
    cart
      .map((item) => (item.branchId != null ? String(item.branchId) : ''))
      .filter(Boolean),
  )
  // Count of cart items already routed to a given branch — used only to
  // decide whether to show the "You already have items to collect from this
  // branch" nudge on the pickup tile. Count itself isn't displayed.
  const basketItemCountForBranch = (branchId: string | number | null) => {
    if (branchId == null) return 0
    return cart.filter(
      (item) => item.branchId != null && String(item.branchId) === String(branchId),
    ).length
  }

  // Nearest branch with any orderable stock — capTotal is the view-computed
  // ceiling (branch + DC), so a single check covers shelf + DC-backed stock.
  // `allBranches` is already nearest-first (basket branches float to the top
  // upstream), so `.find()` returns the closest qualifying branch. Falls back
  // to the head when nothing is stocked so the tile still previews a sensible
  // option. Returns listing.branchId while stock is still loading (null).
  //
  // Deliberately NOT quantity-aware: DEV-50888. The Sept 9 qty-driven swap
  // silently moved the branch when the engineer typed a qty above the
  // current branch's stock — causing orders to go to a different branch
  // than the one shown when the page first loaded. `channelCapacityFor`
  // stays defined below for backorder gating; only this seed is reverted.
  const channelCapacityFor = (
    b: SupplierListing,
    method: FulfillmentMethod | undefined,
  ): number => {
    if (method === 'delivery') {
      if (b.canDeliver === false) return 0
      const primary =
        b.fulfillmentOptions?.find(o => o.type === 'delivery')?.stockQuantity ?? 0
      return Math.max(primary, b.deliveryDcStock ?? 0)
    }
    if (method === 'pickup') {
      if (b.canCollect === false) return 0
      const primary =
        b.branchStock ??
        b.fulfillmentOptions?.find(o => o.type === 'pickup')?.stockQuantity ??
        0
      return Math.max(primary, b.pickupDcStock ?? 0)
    }
    return b.capTotal ?? 0
  }
  const firstStockedBranchId = (() => {
    if (listing.isLoadingStock) return listing.branchId
    const found = allBranches.find(b => (b.capTotal ?? 0) > 0)
    return found?.branchId ?? allBranches[0].branchId
  })()

  const [selectedBranchId, setSelectedBranchId] = useState<string | number | null>(firstStockedBranchId)
  // Tracks whether the user has manually picked a branch — if so, sort changes
  // must not reset their selection (firstStockedBranchId changes when allBranches
  // re-orders on sort change, which would otherwise trigger the effect below).
  const hasManualSelectionRef = useRef(false)

  // Only auto-select when stock loads for the first time (hasManualSelection is false).
  // Sort changes also mutate firstStockedBranchId but must NOT reset a manual pick.
  // No `onBranchChange` bubble here (DEV-50888): the page-level auto-select
  // effect owns the initial-mount seed for the parent's `selection` state,
  // and this seed is now qty-independent so it only advances when stock
  // first loads — never as a side-effect of the engineer typing a quantity.
  useEffect(() => {
    if (!hasManualSelectionRef.current) {
      setSelectedBranchId(firstStockedBranchId)
    }
    setIsBranchSelectorOpen(false)
  }, [firstStockedBranchId])

  // The branch currently displayed on the card
  const activeListing =
    allBranches.find(b => String(b.branchId) === String(selectedBranchId)) ?? allBranches[0]

  // All branches are shown in the Change selector so the engineer can see
  // which branches are out of stock too — zero-stock branches display red 0s.
  // Always sorted by distance (nearest first) so the basket-boost applied
  // to allBranches (used for preselection) doesn't reorder the dropdown.
  const switchableBranches = [...allBranches].sort(
    (a, b) => (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999)
  )

  const isCheckingStock = activeListing.isLoadingStock === true
  // No supplier branch within range of the user's postcode — the product
  // exists but can't be ordered here. Rendered like the unavailable state
  // (dashes, no fulfillment) but with its own message and a hard-disabled
  // add-to-cart / quantity.
  const isNoNearbyBranch = activeListing.noNearbyBranch === true
  const isStockUnavailable =
    activeListing.stockUnavailable === true ||
    !!(activeListing as any).stockError ||
    isNoNearbyBranch

  // Show "Change" only when stock is loaded and there are at least 2 viable branches
  const hasAlternateBranches =
    !isCheckingStock &&
    !isStockUnavailable &&
    switchableBranches.length > 1

  // Order-on-Demand cap: when the supplier surfaces DC stock, the cap is the
  // MAX of branch_stock and dc_stock — not the sum. We can't safely assume
  // branch and DC are independent pools; some suppliers (e.g. CPS Partial
  // options) report a dc_stock that already includes the branch portion, so
  // summing would overcount. Picking the max is the defensive choice that
  // Phase 4: cap is computed at the view layer (branch + pickup_dc_stock).
  // Frontend just reads. Fall back to legacy stockQuantity for any response
  // that pre-dates the new field.
  const maxOrderQty = activeListing.capTotal ?? activeListing.stockQuantity
  // Products sold in fixed multiples (packs / lengths). Displayed as an info
  // pill; the actual multiple enforcement now lives in the page-level stepper.
  const orderMultiple = activeListing.quantityMultiple ?? 1
  // "Each" is the backend's fallback when a supplier (e.g. Wolseley) has no
  // real UoM — swap it for "unit"/"units" so the card doesn't read as
  // "£34.77 / Each of 3". Real UoMs ("Metre", "Litre", …) pass through.
  const rawUom = activeListing.unitOfMeasure
  const isGenericUom = !rawUom || /^each$/i.test(rawUom)
  const perUnitLabel = isGenericUom ? 'unit' : rawUom
  const denominatorLabel =
    orderMultiple > 1
      ? `${orderMultiple} ${isGenericUom ? 'units' : rawUom}`
      : perUnitLabel
  // Supplier prices are quoted per sell-unit (e.g. per pack of 10); base-unit
  // price is derived for the "£X.XXX per unit" secondary line.
  const unitPrice = perUnitPrice(activeListing.price, orderMultiple)
  const pickupOption = activeListing.fulfillmentOptions?.find(o => o.type === 'pickup')
  const deliveryOption = activeListing.fulfillmentOptions?.find(o => o.type === 'delivery')
  // Tile-display source: settled, not live — keeps numbers aligned with the
  // last-fetched DC date.
  const requestedQty = settledQuantity

  // Order-on-Demand: the requested qty needs a DC-backed collection date when
  // the supplier exposes DC stock and the qty sits above branch stock but
  // within the cap. (qty <= branch → collectable now, no date needed;
  // qty > cap → overStock, blocked.)
  const branchNowQty = activeListing.branchStock ?? (pickupOption?.stockQuantity ?? 0)
  const canCollect = activeListing.canCollect !== false
  // The re-check FETCHES both collection + delivery dates, so it must fire
  // regardless of service type — service type only decides which lines render.
  // Gated on `isSelected` so only the currently chosen card fires live
  // supplier calls when the page-level quantity changes — otherwise every
  // rendered card would fan out one bypass-cache request per keystroke.
  const needsDcDate =
    onCheckQuantity != null &&
    isSelected &&
    activeListing.dcStock != null &&
    !isCheckingStock &&
    !isStockUnavailable &&
    quantity > branchNowQty &&
    quantity <= maxOrderQty
  // Backorder above the cap for an API-date supplier (Wolseley): its quoted
  // date is qty-aware, so re-probe at the requested qty. Admin-date suppliers
  // never vary with qty — no call.
  // Gated on the SELECTED channel's capacity (branch + depot for pickup;
  // deliverable + depot for delivery), which is what classifies the line as
  // a backorder below — maxOrderQty is channel-agnostic and can hide a
  // delivery backorder behind surplus collection stock.
  const selectedChannelCapacity = selectedMethod
    ? channelCapacityFor(activeListing, selectedMethod)
    : maxOrderQty
  const needsBackorderDate =
    onCheckQuantity != null &&
    isSelected &&
    activeListing.backorderAllowed === true &&
    activeListing.backorderDateFromApi === true &&
    !isCheckingStock &&
    !isStockUnavailable &&
    quantity > selectedChannelCapacity
  const needsQtyCheck = needsDcDate || needsBackorderDate

  // Trailing debounce + dedup + stale-guard: one live call per settled
  // quantity, never per keystroke/stepper-tap. Resets when the branch switches
  // (qty drops back to 1) or the requested qty leaves the DC range.
  useEffect(() => {
    if (!needsQtyCheck) {
      // Cancel any in-flight probe and clear the "checking" flag, but
      // KEEP dcCollectionDate/dcDeliveryDate and lastCheckedKeyRef.
      // Wiping them lets a needsDcDate false→true churn (e.g. cart
      // mutation flickers isCheckingStock) leave the card with null
      // dates that never refetch: composite key still matches ref, so
      // the guard returns early. Keeping the previous fetch's dates
      // stays safe because showPickupDcTier / showDeliveryDcTier both
      // require pickupOverflowQty > 0 (== needsDcDate is true), so
      // stale-but-preserved dates are never rendered when needsDcDate
      // is false.
      dcCheckSeqRef.current += 1
      setIsCheckingDcDate(false)
      // Exiting the DC range (qty dropped back below branch stock) — snap the
      // tile to live so the DC tier disappears immediately instead of hanging
      // frozen on the old settled overflow qty.
      setSettledQuantity(quantity)
      return
    }
    const checkKey = `${quantity}|${activeListing.branchId ?? ''}|${activeListing.supplierId}|${activeListing.supplierProductCode}`
    if (checkKey === lastCheckedKeyRef.current) return

    // Trailing debounce: nothing visible changes until the user stops.
    // When the timer fires we flip to "checking", clear stale dates, and
    // hit the supplier. Rapid stepper taps or keystrokes reset the timer
    // via the effect cleanup, so mid-press "checking" flashes never render.
    const timer = setTimeout(() => {
      const seq = ++dcCheckSeqRef.current
      setIsCheckingDcDate(true)
      setDcCollectionDate(null)
      setDcDeliveryDate(null)
      setFreshBackorderDates(null)
      // Advance the tile-display qty in the same beat as the API call so the
      // "N from DC" number and the fetched date update together.
      setSettledQuantity(quantity)
      onCheckQuantity!(
        activeListing.supplierId,
        activeListing.supplierProductCode,
        activeListing.branchId,
        quantity,
      )
        .then((result) => {
          if (seq !== dcCheckSeqRef.current) return
          setDcCollectionDate(result.collection)
          setDcDeliveryDate(result.delivery)
          setFreshBackorderDates({
            qty: quantity,
            collection: result.backorderCollection ?? null,
            delivery: result.backorderDelivery ?? null,
          })
          lastCheckedKeyRef.current = checkKey
        })
        .catch(() => {
          if (seq === dcCheckSeqRef.current) {
            setDcCollectionDate(null)
            setDcDeliveryDate(null)
            setFreshBackorderDates(null)
          }
        })
        .finally(() => {
          if (seq === dcCheckSeqRef.current) setIsCheckingDcDate(false)
        })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsQtyCheck,
    quantity,
    activeListing.branchId,
    activeListing.supplierId,
    activeListing.supplierProductCode,
    onCheckQuantity,
  ])

  const handleBranchSelect = (branchId: string | number | null) => {
    hasManualSelectionRef.current = true
    setSelectedBranchId(branchId)
    setIsBranchSelectorOpen(false)
    // Bubble the switch up so the page-level footer (which needs the new
    // branch id to price/submit the order) stays in sync with the branch
    // the engineer just picked here.
    const nextListing =
      allBranches.find(b => String(b.branchId) === String(branchId)) ??
      activeListing
    onBranchChange?.(nextListing)
    // Quantity is governed by the prefill effect (cart line's quantity when the
    // product is in the cart, else 1) — mirroring the supplier detail page,
    // which never resets quantity on a branch change. Hardcoding setQuantity(1)
    // here wrongly dropped the cart quantity to 1 on every branch switch and,
    // because the prefill effect only re-seeds when the resolved line changes
    // reference, it never recovered.
  }

  // Primary delivery follows delivery_stock. dcStock is reserved for the
  // later/DC tier so dc_stock: 0 does not erase branch-backed delivery.
  // A pickup-only branch (canDeliver=false) can't deliver — suppress it.
  const canDeliver = activeListing.canDeliver !== false
  const deliveryQty = canDeliver
    ? (deliveryOption?.stockQuantity ?? 0)
    : 0
  const deliveryDate =
    needsDcDate && dcDeliveryDate ? dcDeliveryDate : deliveryOption?.earliestDate
  const deliveryPrimaryLabel = formatEarliestDate(deliveryOption?.earliestDate)

  // Pickup "available now" = how many of the requested qty are on the branch
  // shelf right now. branchStock for DC suppliers; falls back to the pickup
  // channel quantity for suppliers without a branch/DC split. Also the
  // threshold for the delivery wording (within branch stock vs. DC-backed).
  const collectNow = activeListing.branchStock ?? (pickupOption?.stockQuantity ?? 0)
  const pickupAvailableNow = collectNow
  const exceedsBranchStock = requestedQty > pickupAvailableNow
  const pickupAvailableNowLabel = formatEarliestDate(pickupOption?.earliestDate)
  // Phase 3: LATER quantity comes from the adapter. CPS pre-subtracts the
  // branch portion; Wolseley/UKPS leave it raw. Frontend reads directly,
  // no fallback math — backend is the single source of truth.
  const pickupLaterQty = activeListing.pickupDcStock ?? 0
  const pickupOverflowQty = Math.max(requestedQty - pickupAvailableNow, 0)
  // LATER date rule: when the primary tile already has both stock AND date,
  // LATER stays generic ("Later"). When primary is empty/incomplete, surface
  // the genuine DC date so the engineer knows when it arrives. NEVER fall
  // back to pickupOption.earliestDate (that's the BRANCH date, would surface
  // "Today" wrongly).
  // Phase 2: trust the adapter. pickupDcDate is set by the backend only
  // when a real DC date applies (UKPS/Wolseley: gated on qty > branch_stock;
  // CPS: when a future-dated regular collection option exists). Frontend
  // just reads it. The qty-recheck callback refreshes the value via
  // dcCollectionDate. When null but dcStock > 0, the LATER tile shows a
  // generic "Later" label downstream.
  const hasPickupPrimaryComplete = pickupAvailableNow > 0 && !!pickupOption?.earliestDate
  const pickupDcDate = isCheckingDcDate
    ? null
    : (dcCollectionDate ?? activeListing.pickupDcDate ?? null)
  const pickupDcDateLabel = formatEarliestDate(pickupDcDate)
  const hasPickupLaterDate = pickupDcDateLabel != null
  // When we know DC stock exists but no precise date is available, show a
  // generic SLA-style window so the engineer has a rough expectation
  // rather than a vague "Later".
  const pickupLaterLabel = pickupDcDateLabel ?? '2-5 Business Days'
  // Hide the LATER row when its qty matches the primary tile's qty — the
  // engineer would just see the same number twice (e.g. raw-pool suppliers
  // where dc_stock happens to equal branch_stock).
  const showPickupLaterTier =
    canCollect &&
    activeListing.dcStock != null &&
    pickupLaterQty > 0 &&
    pickupLaterQty !== pickupAvailableNow
  const showPickupDcTier =
    canCollect &&
    activeListing.dcStock != null &&
    pickupOverflowQty > 0 &&
    (pickupDcDateLabel != null || isCheckingDcDate)
  // Show the branch's actual "now" stock, not the engineer's requested qty.
  // Previously clamped to min(requestedQty, pickupAvailableNow), which echoed
  // the input back and hid whether the branch had 15 or 5,000 in stock.
  const pickupNowDisplayQty = pickupAvailableNow
  const pickupLaterDisplayQty = Math.min(pickupOverflowQty, pickupLaterQty)
  const pickupNowSentenceDate = pickupAvailableNowLabel ?? 'Date not available'
  const showPickupPrimaryQuantity = exceedsBranchStock
  // Phase 4: adapter now emits delivery_stock as "branch's deliverable
  // portion only" — no more DC pollution. Frontend reads it directly.
  const hasRealDeliveryData = !!deliveryOption?.earliestDate
  const deliveryPrimaryQty = deliveryQty
  // Phase 3: delivery LATER qty comes from the adapter. No fallback math.
  const deliveryLaterQty = activeListing.deliveryDcStock ?? 0
  const deliveryOverflowQty = Math.max(requestedQty - deliveryPrimaryQty, 0)
  // Delivery LATER date: show the real DC date when EITHER
  //   - qty exceeds branch (engineer needs the DC), OR
  //   - primary tile is incomplete (no stock and/or no date — the only
  //     useful info is the LATER row).
  // Otherwise keep label generic ("Later"). Branch's delivery date is
  // NEVER a fallback.
  // Phase 2: trust the adapter. deliveryDcDate is set by the backend only
  // when a real DC date applies. Same simplification as pickupDcDate above.
  const hasDeliveryPrimaryComplete = deliveryPrimaryQty > 0 && !!deliveryPrimaryLabel
  const deliveryDcDate = isCheckingDcDate
    ? null
    : (dcDeliveryDate ?? activeListing.deliveryDcDate ?? null)
  const deliveryDcDateLabel = formatEarliestDate(deliveryDcDate)
  const hasDeliveryLaterDate = deliveryDcDateLabel != null
  const deliveryLaterLabel = deliveryDcDateLabel ?? '2-5 Business Days'
  // Hide the LATER row when its qty matches the primary tile's qty (same
  // duplicate-row guard as pickup).
  const showDeliveryLaterTier =
    canDeliver &&
    activeListing.dcStock != null &&
    deliveryLaterQty > 0 &&
    deliveryLaterQty !== deliveryPrimaryQty
  const showDeliveryDcTier =
    canDeliver &&
    activeListing.dcStock != null &&
    deliveryOverflowQty > 0 &&
    (deliveryDcDateLabel != null || isCheckingDcDate)
  // Backorder tier — after branch, depot (and, for delivery, the borrowed
  // branch, already folded into deliveryPrimaryQty). Applies when the
  // supplier/product allows it and the requested qty exceeds everything the
  // channel can supply (including zero stock). Shown as ONE date per line.
  const backorderAllowed = activeListing.backorderAllowed === true
  const pickupCapacity = Math.max(pickupAvailableNow, pickupLaterQty)
  const deliveryCapacity = Math.max(deliveryPrimaryQty, deliveryLaterQty)
  // API-date suppliers: prefer the qty-matched probe result over the initial
  // qty-1 date; admin-date suppliers keep the initial (qty-independent) date.
  const useFreshBackorderDates =
    activeListing.backorderDateFromApi === true &&
    freshBackorderDates != null &&
    freshBackorderDates.qty === requestedQty
  const pickupBackorderDate = useFreshBackorderDates
    ? freshBackorderDates.collection
    : (activeListing.backorderCollectionDate ?? null)
  const deliveryBackorderDate = useFreshBackorderDates
    ? freshBackorderDates.delivery
    : (activeListing.backorderDeliveryDate ?? null)
  const isCheckingBackorderDate = isCheckingDcDate && needsBackorderDate
  // A backorder needs an availability date for the channel — without one
  // (e.g. Wolseley's API returned none) the line stays blocked as before.
  // While the qty probe is in flight the tile shows "checking" instead.
  const isPickupBackorder =
    backorderAllowed &&
    canCollect &&
    requestedQty > pickupCapacity &&
    (pickupBackorderDate != null || isCheckingBackorderDate)
  const isDeliveryBackorder =
    backorderAllowed &&
    canDeliver &&
    requestedQty > deliveryCapacity &&
    (deliveryBackorderDate != null || isCheckingBackorderDate)
  const pickupBackorderLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability...'
    return (
      formatBackorderAvailability(pickupBackorderDate) ??
      'Collection date to be confirmed'
    )
  })()
  const deliveryBackorderLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability...'
    return (
      formatBackorderAvailability(deliveryBackorderDate) ??
      'Delivery date to be confirmed'
    )
  })()

  // Single-line availability summaries — matches the mock's "N available today"
  // / "N available today · N from Thursday" pattern. Folds the primary tile
  // stock and the DC LATER row into one string so the tile body stays as
  // compact as the mock while still surfacing DC-tier qty when it applies.
  const pickupSummaryLabel = (() => {
    if (isPickupBackorder) return pickupBackorderLabel
    if (isCheckingDcDate && showPickupDcTier) return 'Checking availability...'
    const parts: string[] = []
    if (canCollect && pickupNowDisplayQty > 0) {
      // When the supplier gives no collection date, say "Collection date to be
      // confirmed" — never "Today" (which the supplier never confirmed). A real
      // date, including a genuine same-day "Today", still renders as the date.
      const hasRealDate = pickupNowSentenceDate !== 'Date not available'
      parts.push(
        hasRealDate
          ? `${pickupNowDisplayQty.toLocaleString()} available ${pickupNowSentenceDate}`
          : `${pickupNowDisplayQty.toLocaleString()} available · Collection date to be confirmed`,
      )
    }
    if (showPickupLaterTier && pickupLaterDisplayQty > 0) {
      parts.push(
        `${pickupLaterDisplayQty.toLocaleString()} from ${pickupLaterLabel}`,
      )
    }
    return parts.length > 0 ? parts.join(' · ') : null
  })()

  // Single-line delivery summary — a delivery is one dispatch, so it gets one
  // date (unlike pickup, which can genuinely happen in two visits/tiers).
  //  - Branch alone fulfils the ask → show branch's actual deliverable stock
  //    (max avail per the ticket) at the fast branch date.
  //  - Otherwise the supplier consolidates from the DC → show the engineer's
  //    qty (capped by total capacity) at the DC date. Raw DC pool size is
  //    noise — a 8,713 number for a 50-unit order buries the useful info.
  const deliverySummaryLabel = (() => {
    if (isDeliveryBackorder) return deliveryBackorderLabel
    if (isCheckingDcDate && showDeliveryDcTier) return 'Checking availability...'
    if (!canDeliver) return null
    if (deliveryCapacity <= 0) return null
    if (requestedQty <= deliveryPrimaryQty && deliveryPrimaryQty > 0) {
      const dateWord = deliveryPrimaryLabel ?? 'Later'
      return `${deliveryPrimaryQty.toLocaleString()} available to deliver from ${dateWord}`
    }
    const dcQty = Math.min(requestedQty, deliveryCapacity)
    // Use deliveryLaterLabel — which resolves to the real DC date when the
    // adapter supplied one, else the generic "2-5 Business Days" window.
    // Never fall back to deliveryPrimaryLabel here: for an overflow that
    // requires DC fulfilment, showing the branch's fast date (often "Today")
    // would advertise a delivery the DC path can't actually hit.
    return `${dcQty.toLocaleString()} available to deliver from ${deliveryLaterLabel}`
  })()

  // Effective per-method date the engineer will actually experience for the
  // requested quantity: DC-tier date when qty exceeds branch stock, else the
  // branch-level earliestDate. Bubbled to the page via `onResolvedDates` so
  // the sticky footer summary reads the same date the card is showing —
  // without this, the footer says "Today" while the card correctly says
  // "Fri 21 Aug" for a DC-tier collection.
  const effectivePickupDate = isPickupBackorder
    ? pickupBackorderDate
    : exceedsBranchStock
      ? (pickupDcDate ?? pickupOption?.earliestDate ?? null)
      : (pickupOption?.earliestDate ?? null)
  const effectiveDeliveryDate = isDeliveryBackorder
    ? deliveryBackorderDate
    : deliveryOverflowQty > 0
      ? (deliveryDcDate ?? deliveryOption?.earliestDate ?? null)
      : (deliveryOption?.earliestDate ?? null)
  useEffect(() => {
    if (!onResolvedDates) return
    if (isCheckingStock) return
    onResolvedDates(activeListing, {
      pickup: effectivePickupDate,
      delivery: effectiveDeliveryDate,
      isChecking: isCheckingDcDate,
      backorderPickup: isPickupBackorder ? pickupBackorderDate : null,
      backorderDelivery: isDeliveryBackorder ? deliveryBackorderDate : null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeListing.branchId,
    activeListing.supplierId,
    effectivePickupDate,
    effectiveDeliveryDate,
    isCheckingStock,
    isCheckingDcDate,
  ])

  // Selection styling uses `brand-primary` tokens so every selection surface
  // on this card (outer card, fulfilment tiles, branch rows) reads as the
  // same signal AND themes to the tenant's brand color. Applied on top of
  // the best-price left border so both signals coexist (the pink stripe
  // still calls out best price even when another card is the selected offer).
  return (
    <div
      className={clsx(
        'bg-white rounded-xl overflow-hidden border border-gray-200',
        isBestPrice && 'border-l-4 border-l-pink-400',
      )}
    >
      <div className="px-3 py-3">

        {/* Product name + price */}
        <div className="flex flex-col min-[400px]:flex-row min-[400px]:items-start min-[400px]:justify-between gap-0.5 min-[400px]:gap-2 mb-1">
          {onClick ? (
            <button
              onClick={() => onClick(activeListing)}
              className="text-left flex-1 min-w-0"
            >
              <h4 className="text-gray-950 font-bold text-sm leading-snug hover:underline">
                {listing.productName}
              </h4>
            </button>
          ) : (
            <h4 className="text-gray-950 font-bold text-sm leading-snug flex-1 min-w-0">
              {listing.productName}
            </h4>
          )}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Linked-product favourite (DEV-46921 UI feedback 16-07-26).
             * The heart favourites this supplier's SKU (the raw product
             * id) so a later tap on the Home Favourites tile reopens
             * this exact supplier's product page. Gated by the build
             * flag so environments without the Home tab don't render
             * a heart that leads to an unreachable /favourites page. */}
            {HOME_TAB_ENABLED && (
              <FavouriteHeartButton
                rawProductId={listing.productId}
                productName={listing.productName}
              />
            )}
            {activeListing.priceUnavailable ? (
              <span className="text-gray-400 font-bold text-sm">£-</span>
            ) : (
              <div className="flex flex-col items-end leading-tight">
                <div>
                  {/* Neutral gray to match the mock — primary color is
                   * reserved for the selection signal (radio dot, ring). */}
                  <span className="text-gray-950 font-bold text-base">£{activeListing.price.toFixed(2)}</span>
                  <span className="text-[10px] text-gray-500"> / {denominatorLabel}</span>
                </div>
                {orderMultiple > 1 && (
                  <span className="text-[10px] text-gray-500">(i.e. £{unitPrice.toFixed(3)} per {perUnitLabel})</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Supplier + badges — single row to match the mock */}
        <div className="mt-1.5 mb-2 flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">{listing.supplierName}</span>
          {listing.isVanStock && <VanStockBadge size="xs" />}
          {/* DEV-51188: summary In Stock / Backorder pill — matches the pill
           * engineers already see on the search card and cart line for the
           * same product. Uses pickupCapacity/deliveryCapacity (branch + DC
           * combined) — the same numbers the tiles below the badge row
           * consume — so a "delivery from DC" supplier reads as IN STOCK
           * even when branch_stock=0. IN STOCK wins over BACKORDER whenever
           * any viable channel has capacity, matching how search flags the
           * master product; BACKORDER only fires when no channel has
           * capacity and at least one is backorder-eligible. Suppressed
           * while stock is loading / unavailable / no nearby branch — those
           * states already have their own explicit tile. */}
          {!isCheckingStock && !isStockUnavailable && !isNoNearbyBranch && (() => {
            // Per-channel classify mirrors getCartItemStockView in
            // utils/cartItemStock.ts: within a channel qty ≤ capacity is
            // in-stock; qty > capacity with a supplier backorder date is
            // backorder; anything else is not a pill state (the tile / red
            // "exceeds" warning already speaks). Uses requestedQty (settled)
            // so the pill agrees with the tile numbers under the badge row —
            // qty stepper + branch selector both re-drive `activeListing`
            // and requestedQty, so the pill recomputes on every change.
            const pickupInStock =
              canCollect && requestedQty > 0 && requestedQty <= pickupCapacity
            const deliveryInStock =
              canDeliver && requestedQty > 0 && requestedQty <= deliveryCapacity
            if (pickupInStock || deliveryInStock) return <InStockBadge />
            // Suppress BACKORDER while the qty-matched backorder probe is
            // in flight for API-date suppliers — isPickupBackorder /
            // isDeliveryBackorder are truthy during the probe (before a
            // real date arrives) so the tile can show "Checking
            // availability...", but the summary pill would otherwise
            // flash BACKORDER then disappear if the probe returns no
            // date. Wait for the confirmed date.
            if ((isPickupBackorder || isDeliveryBackorder) && !isCheckingBackorderDate)
              return <BackorderBadge variant="amber-plain" />
            return null
          })()}
          {listing.isPreferred && (
            <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md font-bold">PREFERRED</span>
          )}
          {isClosest && (
            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md font-bold">CLOSEST</span>
          )}
          {isBestPrice && (
            <span className="text-[10px] bg-pink-50 text-pink-700 px-2 py-0.5 rounded-md font-bold">BEST PRICE</span>
          )}
          {listing.isRestricted && (
            <span className="text-[10px] bg-red-50 text-red-700 px-2 py-0.5 rounded-md font-bold">RESTRICTED</span>
          )}
          {isNoNearbyBranch && (
            <span className="inline-block whitespace-nowrap text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md font-bold">
              No nearby branch
            </span>
          )}
        </div>

        {/* Two-column fulfillment grid */}
        <div className={clsx('space-y-[10px] mb-2', isCheckingDcDate && 'animate-pulse')}>
          {isCheckingStock ? (
            <>
              {(['pickup', 'delivery'] as const).map(type => (
                <div key={type} className="bg-gray-50 rounded-md p-2 animate-pulse">
                  <div className="flex items-center gap-1.5 mb-1">
                    {type === 'pickup'
                      ? <Package className="w-3.5 h-3.5 text-gray-300" />
                      : <Truck className="w-3.5 h-3.5 text-gray-300" />}
                    <span className="text-[11px] font-medium text-gray-300">{type === 'pickup' ? 'Collection' : 'Delivery'}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-16" />
                </div>
              ))}
            </>
          ) : isStockUnavailable ? (
            <>
              {(['pickup', 'delivery'] as const).map(type => (
                <div key={type} className="bg-gray-50 rounded-md p-2" title={(activeListing as any).stockError || undefined}>
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center gap-1.5">
                      {type === 'pickup'
                        ? <Package className="w-3.5 h-3.5 text-gray-300" />
                        : <Truck className="w-3.5 h-3.5 text-gray-300" />}
                      <span className="text-[11px] font-medium text-gray-400">{type === 'pickup' ? 'Collection' : 'Delivery'}</span>
                    </div>
                    <span className="text-xs font-semibold text-gray-300">-</span>
                  </div>
                  <div className="text-[11px] text-gray-400 italic">Not available</div>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* Pickup — radio-select tile */}
              <div
                role="button"
                tabIndex={canCollect ? 0 : -1}
                aria-pressed={isSelected && selectedMethod === 'pickup'}
                aria-disabled={!canCollect}
                onClick={() => canCollect && onSelect(activeListing, 'pickup')}
                onKeyDown={(e) => {
                  if (!canCollect) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(activeListing, 'pickup')
                  }
                }}
                className={clsx(
                  'rounded-xl overflow-hidden text-left transition-colors border',
                  !canCollect && 'cursor-not-allowed',
                  canCollect && 'cursor-pointer',
                  isSelected && selectedMethod === 'pickup'
                    ? 'border-brand-primary'
                    : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <div className="px-3 py-2.5 border-b border-gray-100">
                  <div className="flex items-start gap-2">
                    <span
                      className={clsx(
                        'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                        isSelected && selectedMethod === 'pickup'
                          ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                          : 'border-gray-300',
                      )}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <Package className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-[11px] font-semibold">Collection</span>
                      </div>
                      {pickupSummaryLabel ? (
                        <p className={clsx(
                          'mt-1.5 text-sm font-semibold leading-snug',
                          isCheckingDcDate && showPickupDcTier
                            ? 'italic text-gray-400'
                            : 'text-gray-800',
                        )}>
                          {pickupSummaryLabel}
                        </p>
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-500">
                          Not available for collection.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {activeListing.branchName && (
                  <div className="px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                        <MapPin className="w-3.5 h-3.5 flex-none text-gray-500" />
                        <span className="truncate">
                          {activeListing.branchName}
                          {activeListing.distanceMiles != null && (
                            <> · {activeListing.distanceMiles.toFixed(1)} mi</>
                          )}
                        </span>
                      </p>
                      {basketItemCountForBranch(activeListing.branchId) > 0 && (
                        <p className="mt-0.5 flex items-center gap-1.5 text-[10px] font-semibold text-brand-primary-dark">
                          <ShoppingCart className="w-3 h-3 flex-none" aria-hidden="true" />
                          <span>You already have items to collect from this branch</span>
                        </p>
                      )}
                    </div>
                    {hasAlternateBranches && (
                      <button
                        onClick={(e) => {
                          // Prevent the outer tile's radio-select from firing when
                          // the engineer only wants to open the branch picker.
                          e.stopPropagation()
                          setIsBranchSelectorOpen(prev => !prev)
                        }}
                        className="flex-none px-1 text-xs font-bold text-brand-primary hover:underline"
                      >
                        Change
                      </button>
                    )}
                  </div>
                )}
                {/* Branch selector — nested inside the Pickup tile so the
                    picker visually belongs to Collection (delivery is
                    branch-agnostic). Rows call stopPropagation so tapping
                    a branch doesn't also re-fire the outer tile's
                    radio-select. */}
                {isBranchSelectorOpen && hasAlternateBranches && (
                  <div className="mx-2 mb-2 mt-1 rounded-lg border border-gray-200 overflow-hidden">
                    {switchableBranches.map((branch) => {
                      const isBranchSelected =
                        String(branch.branchId) === String(activeListing.branchId)
                      // Pickup stock: respect canCollect first (delivery-only
                      // branch shows 0). Falls back to the pickup channel's
                      // stockQuantity (which represents branch shelf stock
                      // for collection).
                      const branchPickup =
                        branch.canCollect === false
                          ? 0
                          : (branch.fulfillmentOptions?.find(
                              o => o.type === 'pickup',
                            )?.stockQuantity ?? 0)
                      const hasCartItems = basketBranchIds.has(
                        String(branch.branchId),
                      )
                      return (
                        <button
                          key={String(branch.branchId)}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleBranchSelect(branch.branchId)
                          }}
                          className={clsx(
                            'flex w-full items-center justify-between gap-3 border-t border-gray-100 px-2.5 py-1.5 text-left transition-colors first:border-t-0',
                            isBranchSelected ? 'bg-gray-50' : 'bg-white hover:bg-gray-50',
                          )}
                        >
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-900">
                                {branch.branchName}
                              </span>
                              {hasCartItems && (
                                <ShoppingCart
                                  className="w-3 h-3 text-brand-primary flex-shrink-0"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                            {branch.distanceMiles != null && (
                              <span className="mt-0.5 block text-[10px] text-gray-600">
                                {branch.distanceMiles.toFixed(1)} mi
                              </span>
                            )}
                          </span>
                          <span
                            className={clsx(
                              'max-w-32 text-right text-[10px] font-semibold',
                              branchPickup > 0
                                ? 'text-gray-700'
                                : 'text-red-500',
                            )}
                          >
                            {branchPickup > 0 ? branchPickup.toLocaleString() : 'Out of stock'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              {/* Delivery — radio-select tile */}
              <div
                role="button"
                tabIndex={canDeliver ? 0 : -1}
                aria-pressed={isSelected && selectedMethod === 'delivery'}
                aria-disabled={!canDeliver}
                onClick={() => canDeliver && onSelect(activeListing, 'delivery')}
                onKeyDown={(e) => {
                  if (!canDeliver) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(activeListing, 'delivery')
                  }
                }}
                className={clsx(
                  'rounded-xl overflow-hidden text-left transition-colors border',
                  !canDeliver && 'cursor-not-allowed',
                  canDeliver && 'cursor-pointer',
                  isSelected && selectedMethod === 'delivery'
                    ? 'border-brand-primary'
                    : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <div className="px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <span
                      className={clsx(
                        'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                        isSelected && selectedMethod === 'delivery'
                          ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                          : 'border-gray-300',
                      )}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <Truck className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-[11px] font-semibold">Delivery</span>
                      </div>
                      {deliverySummaryLabel ? (
                        <p className={clsx(
                          'mt-1.5 text-sm font-semibold leading-snug',
                          isCheckingDcDate && showDeliveryDcTier
                            ? 'italic text-gray-400'
                            : 'text-gray-800',
                        )}>
                          {deliverySummaryLabel}
                        </p>
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-500">
                          Not available for delivery.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Product info accordion */}
        <div>
          <button
            onClick={() => setIsProductInfoOpen(prev => !prev)}
            className={clsx(
              'w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 transition-colors',
              isProductInfoOpen ? 'rounded-t-lg' : 'rounded-lg'
            )}
          >
            <span className="text-xs font-semibold text-gray-600">Product information</span>
            <ChevronDown className={clsx(
              'w-3.5 h-3.5 text-gray-400 transition-transform duration-200',
              isProductInfoOpen && 'rotate-180'
            )} />
          </button>
          <div className={clsx(
            'grid transition-all duration-200 ease-in-out',
            isProductInfoOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}>
            <div className="overflow-hidden">
              <div className="px-3 py-2.5 bg-gray-50 rounded-b-lg flex gap-3">
                <div className="w-14 h-14 bg-white rounded-lg border border-gray-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {listing.imageUrl && !imageError ? (
                    <img
                      src={listing.imageUrl}
                      alt={listing.productName}
                      className="w-full h-full object-contain"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <Package className="w-6 h-6 text-gray-300" />
                  )}
                </div>
                <div className="flex-1 text-xs space-y-1">
                  {/* Brand (DEV-51596) — per-supplier value with dash
                      fallback (AC2). Lives inside the Product info section
                      per Noreen's direction so every page carries brand in
                      the same place. */}
                  <p>
                    <span className="text-gray-400">Brand:</span>{' '}
                    <span className="text-gray-700 font-medium">{listing.brand || '-'}</span>
                  </p>
                  {listing.manufacturerCode && (
                    <p>
                      <span className="text-gray-400">Code:</span>{' '}
                      <span className="text-gray-700 font-medium">{listing.manufacturerCode}</span>
                    </p>
                  )}
                  <p>
                    <span className="text-gray-400">UOM:</span>{' '}
                    <span className="text-gray-700 font-medium">{activeListing.unitOfMeasure || 'Each'}</span>
                  </p>
                  {listing.productDescription && (
                    <p className="text-gray-500 leading-relaxed line-clamp-3 pt-0.5">
                      {listing.productDescription}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Info banners — static per-listing signals. Quantity-driven warnings
       * (over-stock, invalid-multiple, running totals) live on the page-level
       * sticky footer alongside the stepper. */}
      {(activeListing.priceUnavailable ||
        (!isNoNearbyBranch && isStockUnavailable)) && (
        <div className="px-3 pb-3 -mt-1 space-y-1.5">
          {activeListing.priceUnavailable && (
            <p className="text-amber-600 text-xs">Price unavailable</p>
          )}
          {!isNoNearbyBranch && isStockUnavailable && (
            <p className="text-amber-600 text-xs">Out of stock</p>
          )}
        </div>
      )}
    </div>
  )
}


/**
 * Small per-card heart control (DEV-46921 UI feedback 16-07-26).
 *
 * Kept as a local sub-component so the parent card's render stays lean
 * — the favourites hook subscription only lives on the heart itself,
 * not on the whole listing card. Favourites the raw product (this
 * supplier's SKU) so a later tap on the Home Favourites tile reopens
 * this exact supplier product.
 */
function FavouriteHeartButton({
  rawProductId,
  productName,
}: {
  rawProductId: string | number
  productName: string
}) {
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()
  const target = { type: 'raw' as const, id: rawProductId }
  const active = isFavourited(target)
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        toggleFavourite({ target, productName })
      }}
      // The ::before extends the tap area to 44px without changing the look.
      className="relative p-1 -m-1 rounded-md hover:bg-gray-100 transition-colors before:absolute before:-inset-3 before:content-['']"
      aria-label={active ? 'Remove from favourites' : 'Add to favourites'}
      aria-pressed={active}
    >
      <Heart
        className={clsx(
          'w-4 h-4',
          active ? 'text-brand-primary' : 'text-gray-400',
        )}
        fill={active ? 'currentColor' : 'none'}
      />
    </button>
  )
}

